/* eslint-disable no-async-promise-executor */
import { openDB } from 'idb';
import { useOfflineModeStore } from '@/stores/offlineModeStore';

import { saveShow, submitNewShow } from '@/services/ShowsService';

import API from '@/assets/libraries/API_Requests';
import { setToLocalStorage } from '@/helpers/localStorage';
import { storage_management_service } from './StorageManagementService';
import { useShowStore } from '@/stores/show';
import { SAVE_TRIGGERED_FROM } from '@/constants';
import { deTransformScriptArray, getShowDataObjectForHash, sha256HashOfObject } from '@/helpers/commonFunctions';

const offline_mode_store = () => {
	return useOfflineModeStore();
};

window.addEventListener('online', () => {
	console.log('[Network] Went Online');
	offline_mode_store().is_online = true;
	OfflineModeEditsPendingSyncManager.syncOfflineEdits();
});

window.addEventListener('offline', () => {
	console.log('[Network] Went Offline');
	offline_mode_store().is_online = false;
});

export const isOffline = () => {
	return offline_mode_store().is_online == false;
};

export const isOnline = () => {
	return offline_mode_store().is_online;
};

export const dbPromise = openDB('csc_offline_database', 5, {
	upgrade(db) {
		// if (!db.objectStoreNames.contains('settings')) {
		// 	db.createObjectStore('settings', { keyPath: 'name' });
		// }
		if (!db.objectStoreNames.contains('shows')) {
			const store = db.createObjectStore('shows', { keyPath: 'show_ID' });
			store.createIndex('created_offline', 'created_offline', { unique: false });
		}
		if (!db.objectStoreNames.contains('show_revisions')) {
			const store = db.createObjectStore('show_revisions', { keyPath: 'revision_id' });
			store.createIndex('show_ID', 'show_ID', { unique: false });
			store.createIndex('created_offline', 'created_offline', { unique: false });
		}
		if (!db.objectStoreNames.contains('audio_files')) {
			db.createObjectStore('audio_files', { keyPath: 'file_ID' });
		}
		if (!db.objectStoreNames.contains('audio_files_info')) {
			db.createObjectStore('audio_files_info', { keyPath: 'file_ID' });
		}
		if (!db.objectStoreNames.contains('my_fireworks')) {
			db.createObjectStore('my_fireworks', { keyPath: 'firework_ID' });
		}
		if (!db.objectStoreNames.contains('offline_edits_pending_sync')) {
			db.createObjectStore('offline_edits_pending_sync', { keyPath: 'edit_key' });
		}
		if (!db.objectStoreNames.contains('offline_edits_synced')) {
			db.createObjectStore('offline_edits_synced', { keyPath: 'id' });
		}
		if (!db.objectStoreNames.contains('misc_data')) {
			db.createObjectStore('misc_data', {});
		}
	},
	blocked() {
		console.log('[DB] Database blocked');
	},
	blocking() {
		console.log('[DB] Database blocking');
	},
	terminated() {
		console.log('[DB] Database terminated');
	},
});

export const _addRow = async (storeName, value) => {
	const db = await dbPromise;
	const tx = db.transaction(storeName, 'readwrite');
	await tx.objectStore(storeName).add(value);
	await tx.done;
};

export const _getRow = async (storeName, key) => {
	const db = await dbPromise;
	return db.transaction(storeName).objectStore(storeName).get(key);
};

export const _getAllRows = async (storeName) => {
	const db = await dbPromise;
	return db.transaction(storeName).objectStore(storeName).getAll();
};

export const _getAllRowsForKeyViaIndex = async (storeName, indexName, key) => {
	const db = await dbPromise;
	return db.transaction(storeName).objectStore(storeName).index(indexName).getAll(key);
};
export const _updateRow = async (database_name, value, key = undefined, uploading_blob_file = false) => {
	// console.log('check value', value);
	value = uploading_blob_file ? value : JSON.parse(JSON.stringify(value));
	const db = await dbPromise;
	const tx = db.transaction(database_name, 'readwrite');
	const store = tx.objectStore(database_name);

	if (key != undefined) {
		await store.put(value, key);
	} else {
		await store.put(value);
	}

	await handleStorageQuotaExceeded();
	return tx.done;
};

export const _deleteRow = async function (database_name, key) {
	const db = await dbPromise;
	const tx = db.transaction(database_name, 'readwrite');
	const store = tx.objectStore(database_name);

	if (key != undefined) {
		await store.delete(key);
	}

	return tx.done;
};

//MARK: Show Storage
export const OfflineModeShowManager = {};

OfflineModeShowManager.getAllShows = function () {
	return _getAllRows('shows');
};

OfflineModeShowManager.getShow = function (show_id) {
	return new Promise((resolve, reject) => {
		_getRow('shows', show_id)
			.then((show) => {
				return resolve(show);
			})
			.catch((error) => {
				return reject(error);
			});
	});
};

// OfflineModeShowManager.getAllShowsEditingWhileOffline = function () {
// 	console.log('Getting all shows edited while offline');
// 	let shows_edited_while_offline = [];

// 	return new Promise((resolve, reject) => {
// 		database
// 			.then(function (database) {
// 				var tx = database.transaction('shows', 'readonly');
// 				var store = tx.objectStore('shows');

// 				return store.openCursor();
// 			})
// 			.then(function offlineItems(cursor) {
// 				//Loop over the items
// 				if (!cursor) {
// 					return;
// 				}

// 				// console.log('Cursor at: ', cursor.key, cursor.value)
// 				if (cursor.value.saved_offline == true) {
// 					shows_edited_while_offline.push(cursor.value);
// 				}

// 				return cursor.continue().then(offlineItems);
// 			})
// 			.then(() => {
// 				console.log('Done finding shows editing offline', shows_edited_while_offline);
// 				return resolve(shows_edited_while_offline);
// 			})
// 			.catch((error) => {
// 				console.log('[DB] Failed to get all items that were edited offline', error);
// 				return reject(error);
// 			});
// 	});
// };

OfflineModeShowManager.updateShow = function (show_object) {
	return new Promise(async (resolve, reject) => {
		if (show_object == undefined || null) {
			console.log('[Update DB Show] Show_object can not be undefined; To delete a show use the delete show function.');
			return reject();
		}

		let error_getting_show = undefined;
		var show = await _getRow('shows', show_object.show_ID).catch((error) => {
			error_getting_show = error;
		});
		if (error_getting_show != undefined) {
			return reject(error_getting_show);
		}

		if (show != undefined) {
			show = { ...show, ...show_object };
		} else {
			show = show_object;
		}

		_updateRow('shows', show)
			.then(() => {
				return resolve();
			})
			.catch((error) => {
				return reject(error);
			});
	});
};

// OfflineModeShowManager.markShowSynced = function (show_id) {
// 	return new Promise((resolve, reject) => {
// 		database
// 			.then(async function (database) {
// 				var tx = database.transaction('shows', 'readwrite');
// 				var store = tx.objectStore('shows');

// 				var show = await store.get(show_id);

// 				show.saved_offline = false;

// 				store.put(show);
// 				return tx.complete;
// 			})
// 			.then(function () {
// 				console.log('[DB] Successfully marked show as synced');
// 				return resolve();
// 			})
// 			.catch((error) => {
// 				console.log('[DB] Failed to mark show as synced in database', error);
// 				return reject(error);
// 			});
// 	});
// };

// OfflineModeShowManager.deleteShow = function(show_id) {
//      return _deleteRow('shows', show_id)
// }

//MARK: Offline Edits Storage
export const OfflineModeEditsPendingSyncManager = {};

OfflineModeEditsPendingSyncManager.getAllEditsPendingSync = function () {
	return _getAllRows('offline_edits_pending_sync');
};

OfflineModeEditsPendingSyncManager.saveOfflineEdit = function ({ entity, entity_id, action = 'create', entity_data }) {
	return new Promise(async (resolve, reject) => {
		if (entity == undefined || entity_id == undefined || entity_data == undefined) {
			console.log('[Update DB Offline Edit] entity, entity_id, and entity_data can not be undefined.', { entity, entity_id, entity_data });
			return reject();
		}

		let edit_key = `${entity}.${entity_id}.${action}`;
		let edit = {
			edit_key,
			entity,
			entity_id,
			action,
			entity_data,
			edit_created_on: new Date().getTime(),
		};

		_updateRow('offline_edits_pending_sync', edit)
			.then(() => {
				return resolve();
			})
			.catch((error) => {
				return reject(error);
			});
	});
};

OfflineModeEditsPendingSyncManager.markEditSynced = function ({ entity, entity_id, action = 'create', sync_data, user_skipped_sync = false, callback_post_sync = undefined, assumed_synced_after_later_save_was_successful = false }) {
	return new Promise(async (resolve, reject) => {
		if (entity == undefined || entity_id == undefined) {
			console.log('[Update DB Offline Edit] entity and entity_id can not be undefined.');
			return reject();
		}

		let edit_key = `${entity}.${entity_id}.${action}`;

		let error_getting_pending_edit = undefined;
		var pending_edit = await _getRow('offline_edits_pending_sync', edit_key).catch((error) => {
			error_getting_pending_edit = error;
		});
		if (error_getting_pending_edit != undefined) {
			return reject(error_getting_pending_edit);
		}
		if (pending_edit == undefined) {
			return resolve();
		}

		let error_storing_synced_edit = undefined;
		await _updateRow('offline_edits_synced', {
			id: new Date().getTime(),
			edit: pending_edit,
			sync_data,
			synced_on: new Date().getTime(),
			user_skipped_sync,
			assumed_synced_after_later_save_was_successful,
		})
			.then(() => {
				callback_post_sync?.();
			})
			.catch((error) => {
				error_storing_synced_edit = error;
			});

		if (error_storing_synced_edit != undefined) {
			return reject(error_storing_synced_edit);
		}

		_deleteRow('offline_edits_pending_sync', edit_key)
			.then(() => {
				return resolve();
			})
			.catch((error) => {
				return reject(error);
			});
	});
};

async function validateInternetConnection() {
	return API.Call('GET', `${API.COBRA_PORTAL_API_URL}/status`);
}

OfflineModeEditsPendingSyncManager.syncOfflineEdits = async function () {
	if (offline_mode_store().is_syncing_offline_edits == true) {
		console.log('Already syncing offline edits, returning existing promise');
		return offline_mode_store().offline_mode_sync_promise;
	}
	console.log('[Sync] Checking network status before syncing...');

	// Check if we need to wait for active show to be set
	const show_store = useShowStore();
	const offline_edits = await OfflineModeEditsPendingSyncManager.getAllEditsPendingSync();

	// Check if any of the offline edits are for shows that need an active show
	const has_show_edits = offline_edits.some((edit) => edit.entity === 'show');

	if (has_show_edits) {
		// Check if we have an active show with show_ID
		if (show_store.active_show && show_store.active_show.show_ID) {
			console.log('[Sync] Active show is set, proceeding with sync...');
		} else {
			console.log('[Sync] No active show with show_ID found, but show edits exist. Proceeding with sync anyway...');
		}
	}

	let number_of_retries = -1;
	while (number_of_retries < 6) {
		//Only retry 5 times
		number_of_retries++;
		if (number_of_retries >= 5) {
			console.log('[Network] Hit max retry, syncing edits anyway...');
			await OfflineModeEditsPendingSyncManager._performSync();
			break; // exit the loop once synced
		}

		const connection_response = await validateInternetConnection();
		console.log('connection_response', connection_response);

		if (connection_response?.error?.title !== 'NO_NETWORK_CONNECTION') {
			console.log('[Network] Online now, syncing edits...');
			await OfflineModeEditsPendingSyncManager._performSync();
			break; // exit the loop once synced
		} else {
			let start = new Date().getTime();
			console.log('[Network] Offline, retrying...');
			await new Promise((resolve) => setTimeout(resolve, 1000));
			console.log('waited', new Date().getTime() - start);
		}
	}
};

OfflineModeEditsPendingSyncManager._performSync = function () {
	if (offline_mode_store().is_syncing_offline_edits == true) {
		console.log('Already syncing offline edits, returning existing promise');
		return offline_mode_store().offline_mode_sync_promise;
	}

	offline_mode_store().offline_mode_sync_promise = new Promise(async (resolve) => {
		console.log('online?', offline_mode_store().is_online);
		if (offline_mode_store().is_online != true) {
			//Can't sync offline edits if not online
			return resolve();
		}

		const offline_edits = await OfflineModeEditsPendingSyncManager.getAllEditsPendingSync();
		if (offline_edits.length == 0) {
			console.log('No offline edits to sync');
			return resolve();
		}

		offline_mode_store().is_syncing_offline_edits = true;
		offline_mode_store().offline_mode_sync_progress = 0;
		offline_mode_store().is_offline_mode_syncing_modal_open = true;
		let showed_syncing_modal_at = new Date().getTime();

		let sync_stats = {
			number_of_shows_updated: 0,
			number_of_shows_created: 0,
			number_of_edits_not_handled: 0,
			number_of_edits_processed: 0,
		};
		let updated_show_ids = {};

		offline_edits.sort((a, b) => a.edit_created_on - b.edit_created_on);

		offline_mode_store().offline_mode_sync_edits = offline_edits;

		let errors = [];

		for (const edit of offline_mode_store().offline_mode_sync_edits) {
			console.log('edit', edit);
			edit.is_syncing = true;

			if (edit.entity == 'show') {
				if (edit.action == 'create') {
					let temp_show_data = JSON.parse(JSON.stringify(edit.entity_data));
					delete temp_show_data.show_ID;
					delete temp_show_data.show_id;

					await submitNewShow(temp_show_data)
						.then((response) => {
							if (response?.error != undefined) {
								throw new Error(JSON.stringify(response.error));
							}

							if (response?.show?.show_ID != undefined) {
								updated_show_ids[edit.entity_id] = response?.show?.show_ID;
							}

							sync_stats.number_of_shows_created++;
							OfflineModeEditsPendingSyncManager.markEditSynced({ entity: edit.entity, entity_id: edit.entity_id, action: edit.action, sync_data: { new_show_id: response?.show?.show_ID, response } });
						})
						.catch((error) => {
							console.log('Failed to create show while syncing', error);
							sync_stats.number_of_edits_not_handled++;
							edit.failed_to_sync = true;
						});

					edit.is_syncing = false;
					edit.finished_syncing = true;
					sync_stats.number_of_edits_processed++;
					offline_mode_store().offline_mode_sync_progress = sync_stats.number_of_edits_processed / offline_edits.length;
					continue;
				} else if (edit.action == 'update') {
					if (updated_show_ids[edit.entity_data?.show_ID] != undefined) {
						//Update show_id to handle the case where the show was created offline and then updated offline
						edit.entity_data.show_ID = updated_show_ids[edit.entity_data.show_ID];
					}

					await saveShow({ ...edit.entity_data, save_triggered_from: SAVE_TRIGGERED_FROM.OFFLINE_SYNC })
						.then(async (response) => {
							let user_skipped_sync = false;
							let callback_post_sync = undefined;

							if (response?.error != undefined) {
								throw new Error(JSON.stringify(response.error));
							}

							// NOTE: uncomment for debugging
							// try {
							// 	submitShowRevisionLog({ show_id: edit.entity_data.show_ID, was_successful: 1, was_offline_sync: 1, events: edit.entity_data.cues, error: null });
							// } catch (error) {
							// 	console.log('Error submitting show revision log');
							// }

							if (response.status == 'CONFLICT_DETECTED') {
								const show_store = useShowStore();
								const modal_result = await show_store.setForceUpdateShowDetails({
									...edit.entity_data,
									is_offline_sync: true,
									last_edited_on_server: response.last_edited,
									cloud_events_count: response.cloud_events_count,
								});

								user_skipped_sync = modal_result?.skipped == true;
								callback_post_sync = modal_result?.callback_post_sync;

								// if was_sync_successful == false, then throw error so catch block will be executed
								if (modal_result?.was_sync_successful !== true) {
									throw new Error('Show sync failed');
								}
							}

							sync_stats.number_of_shows_updated++;
							OfflineModeEditsPendingSyncManager.markEditSynced({ entity: edit.entity, entity_id: edit.entity_id, action: edit.action, user_skipped_sync, callback_post_sync });
						})
						.catch(async (error) => {
							console.log('Failed to update show while syncing', error);

							// first update the offline_edits-pending_sync table to mark the edit as failed by adding a new column i.e sync_failed
							await _updateRow('offline_edits_pending_sync', { ...edit, sync_failed: true });

							// NOTE: uncomment for debugging
							// try {
							// 	const error_object = {
							// 		message: error.message,
							// 		stack: error.stack,
							// 		name: error.name,
							// 	};
							// 	submitShowRevisionLog({ show_id: edit.entity_data.show_ID, was_successful: 0, was_offline_sync: 1, events: edit.entity_data.cues, error: error_object });
							// } catch (error) {
							// 	console.log('Error submitting show revision log');
							// }

							sync_stats.number_of_edits_not_handled++;
							errors.push({ edit_key: edit.edit_key, error: error });
							edit.failed_to_sync = true;
						});

					edit.is_syncing = false;
					edit.finished_syncing = true;
					sync_stats.number_of_edits_processed++;
					offline_mode_store().offline_mode_sync_progress = sync_stats.number_of_edits_processed / offline_edits.length;
					continue;
				}
			}

			sync_stats.number_of_edits_not_handled++;
			edit.is_syncing = false;
			edit.finished_syncing = true;
			edit.failed_to_sync = true;
			sync_stats.number_of_edits_processed++;
			offline_mode_store().offline_mode_sync_progress = sync_stats.number_of_edits_processed / offline_edits.length;
		}

		const time_modal_has_been_shown = showed_syncing_modal_at - showed_syncing_modal_at;
		if (time_modal_has_been_shown < 4000) {
			//Make sure the modal is shown for at least 4 seconds
			setTimeout(() => {
				offline_mode_store().is_offline_mode_syncing_modal_open = false;
			}, 4000 - time_modal_has_been_shown);
		} else {
			offline_mode_store().is_offline_mode_syncing_modal_open = false;
		}

		offline_mode_store().is_syncing_offline_edits = false;
		sync_stats.offline_edits = offline_edits;

		console.log('sync_stats', sync_stats);
		if (sync_stats.number_of_edits_processed == sync_stats.offline_edits.length && sync_stats.number_of_edits_not_handled == 0) {
			setToLocalStorage('offline_edits_last_synced', new Date().getTime());
			storage_management_service.setLastSuccessfulSyncTimestamp();
		}

		storage_management_service.checkAndResetEmergencyModal();
		return resolve(sync_stats);
	});

	return offline_mode_store().offline_mode_sync_promise;
};

OfflineModeEditsPendingSyncManager.handleCleaningUpOfflineEditsPendingSync = async function (active_show) {
	// after successful save, check if same show exist in offline_edits_pending_sync table, if yes
	// Remove case 1. if it does and it does not contain was_sync_successful column as false then mark it as synced with new column i.e assumed_synced_after_later_save_was_successful as true
	const key = `show.${active_show.show_ID}.update`;
	const target_pending_edit = await _getRow('offline_edits_pending_sync', key);
	if (target_pending_edit != undefined) {
		console.log('target_pending_edit : ', target_pending_edit, ' key : ', key, ' result : ', target_pending_edit.sync_failed);
		if (target_pending_edit.sync_failed !== true) {
			OfflineModeEditsPendingSyncManager.markEditSynced({
				entity: target_pending_edit.entity,
				entity_id: target_pending_edit.entity_id,
				action: target_pending_edit.action,
				assumed_synced_after_later_save_was_successful: true,
			});
		} else {
			// Remove case 2. if the hashes of the show_data and the show_data in offline_edits_pending_sync table are same then remove it
			// Generate hash of current active show data (cloud version)
			const detransformed_active_show = deTransformScriptArray(active_show);
			const active_show_data_for_hash = getShowDataObjectForHash(detransformed_active_show[0]);
			const current_show_hash = await sha256HashOfObject(active_show_data_for_hash);

			// Generate hash of the offline pending edit data
			const offline_edit_data_for_hash = getShowDataObjectForHash(target_pending_edit.entity_data);
			const offline_edit_hash = await sha256HashOfObject(offline_edit_data_for_hash);

			console.log('[Hash Comparison] Current show hash:', current_show_hash, active_show_data_for_hash);
			console.log('[Hash Comparison] Offline edit hash:', offline_edit_hash, offline_edit_data_for_hash);

			if (current_show_hash === offline_edit_hash) {
				console.log('[Hash Comparison] Hashes match! Marking as synced with assumed_synced_after_later_save_was_successful=true');
				OfflineModeEditsPendingSyncManager.markEditSynced({
					entity: target_pending_edit.entity,
					entity_id: target_pending_edit.entity_id,
					action: target_pending_edit.action,
					assumed_synced_after_later_save_was_successful: true,
				});
			} else {
				console.log('[Hash Comparison] Hashes do not match. Keeping pending edit for next sync attempt.');
			}
		}
	}
};

//MARK: Misc Data Storage
export const OfflineModeMiscDataStorage = {};

OfflineModeMiscDataStorage.ENTITY_TYPES = { SETTINGS: 'SETTINGS', USER_DETAILS: 'USER_DETAILS', SUBSCRIPTIONS: 'SUBSCRIPTIONS' };

// OfflineModeMiscDataStorage.getAllShows = function () {
// 	return _getAllRows('shows');
// };

OfflineModeMiscDataStorage.getDataForEntityType = function (entity_type) {
	return new Promise((resolve, reject) => {
		_getRow('misc_data', entity_type)
			.then((data) => {
				return resolve(data);
			})
			.catch((error) => {
				return reject(error);
			});
	});
};

OfflineModeMiscDataStorage.updateEntity = function ({ entity_type, entity_data }) {
	return new Promise(async (resolve, reject) => {
		if (entity_type == undefined || entity_data == undefined) {
			console.log('[Update DB Misc Data] entity_type and entity_data can not be undefined;');
			return reject();
		}

		_updateRow('misc_data', entity_data, entity_type)
			.then(() => {
				return resolve();
			})
			.catch((error) => {
				return reject(error);
			});
	});
};

OfflineModeMiscDataStorage.deleteEntity = function (entity_type) {
	return _deleteRow('misc_data', entity_type);
};

export const OfflineAudioManager = {};

OfflineAudioManager.getAudioFileBlobFromURL = function (url) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('get', url, true);
		xhr.responseType = 'blob';
		xhr.onload = () => {
			return resolve(xhr.response);
		};
		xhr.onerror = (error) => {
			return reject(error);
		};
		xhr.send();
	});
};

OfflineAudioManager.updateAudioFile = function (info, blob, audio_file_id) {
	console.log('[DB] info', info, blob, audio_file_id);
	return new Promise(async (resolve, reject) => {
		if (info == undefined && blob == undefined) {
			console.log('[Update DB Show] info and audio buffer can not be undefined; To delete a audio file use the delete file function.');
			return reject();
		}

		if (info != undefined) {
			var file_info = await _getRow('audio_files_info', audio_file_id).catch((error) => {
				console.log("[DB] File doesn't exist or their was an error", error);
			});

			if (file_info != undefined) {
				file_info = { ...file_info, ...info };
			} else {
				file_info = info;
			}

			await new Promise((resolve, reject) => {
				_updateRow('audio_files_info', file_info)
					.then(() => {
						return resolve();
					})
					.catch((error) => {
						return reject(error);
					});
			});
		}

		if (blob == undefined) {
			return resolve();
		}

		const blob_object = { blob, file_ID: audio_file_id };

		console.log('blob object', blob_object, typeof blob_object.blob, blob_object.blob.size);

		_updateRow('audio_files', blob_object, undefined, true)
			.then(() => {
				return resolve();
			})
			.catch((error) => {
				console.error('Error updating audio file', error);
				return reject(error);
			});
	});
};

export async function getObjectStoreSizes() {
	const req = indexedDB.databases();
	req.then((dbs) => {
		const yourDB = dbs.find((db) => db.name === 'csc_offline_database');
		console.log(`All versions: ${yourDB.version}`);
	});
}

// Show data storage during loading - When getShow() is called in ShowsService.js (line 370), it calls OfflineModeShowManager.updateShow(show[0]) which internally calls _updateRow to store the loaded show data locally in IndexedDB
export async function handleStorageQuotaExceeded() {
	console.log('handleStorageQuotaExceeded called ');
	await storage_management_service.manageStorage();
}
